import { type FC } from "react";

interface FirstSyncLoadingProps {
  accountName: string;
  institutionLogo?: string | null;
}

const Bone: FC<{ className?: string }> = ({ className }) => (
  <div className={`animate-pulse rounded-md bg-stone-200 ${className ?? ""}`} />
);

export const FirstSyncLoading: FC<FirstSyncLoadingProps> = ({ accountName, institutionLogo }) => {
  const initial = accountName.charAt(0).toUpperCase();
  const logoSrc = institutionLogo ? `data:image/png;base64,${institutionLogo}` : null;

  return (
    <div className="relative flex min-w-0 flex-col pt-8 sm:pt-10">
      <div className="flex flex-col gap-6 sm:gap-10">
        <div className="flex items-center gap-4">
          <Bone className="h-10 w-10 rounded-[5px]" />
          <div className="flex flex-1 flex-col gap-1.5">
            <Bone className="h-4 w-40" />
            <Bone className="h-3 w-56" />
          </div>
          <Bone className="h-9 w-24 rounded-md" />
          <Bone className="h-9 w-9 rounded-md" />
        </div>

        <div className="flex flex-col rounded-lg border border-stone-200 sm:flex-row">
          <div className="flex-1 p-4">
            <Bone className="mb-2 h-3 w-20" />
            <Bone className="h-7 w-28" />
          </div>
          <div className="p-4 sm:w-[180px]">
            <Bone className="mb-2 h-3 w-16" />
            <Bone className="h-6 w-24" />
          </div>
          <div className="p-4 sm:w-[180px]">
            <Bone className="mb-2 h-3 w-16" />
            <Bone className="h-6 w-24" />
          </div>
        </div>

        <div className="flex flex-col rounded-lg border border-stone-200">
          <div className="flex items-center gap-3 border-b border-stone-200 px-4 py-3">
            <Bone className="h-4 w-24" />
            <Bone className="h-4 w-32" />
            <Bone className="h-4 w-24" />
            <div className="flex-1" />
            <Bone className="h-4 w-20" />
            <Bone className="h-4 w-20" />
          </div>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4 border-b border-stone-200 px-4 py-3.5 last:border-0">
              <Bone className="h-3 w-20" />
              <Bone className="h-3 w-48" />
              <div className="flex-1" />
              <Bone className="h-5 w-20 rounded-full" />
              <Bone className="h-3 w-20" />
            </div>
          ))}
        </div>
      </div>

      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/60">
        <div className="flex w-full max-w-sm flex-col gap-6 rounded-xl border border-primary bg-white p-6 shadow-lg">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-subtle">
              {logoSrc ? (
                <img
                  src={logoSrc}
                  alt={`${accountName} logo`}
                  className="h-8 w-8 object-contain"
                />
              ) : (
                <span className="text-sm font-semibold text-primary">{initial}</span>
              )}
            </div>
            <p className="text-sm font-semibold text-primary">{accountName}</p>
          </div>

          <div className="flex flex-col gap-3">
            <p className="text-sm text-secondary">Bringing in transactions...</p>
            <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-background">
              <div className="absolute inset-y-0 left-0 rounded-full bg-brand animate-progress-indeterminate" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
